import * as fromToMotion from './motion/fromToMotion';
import * as resetMotion from './motion/resetMotion';

export { Chart } from './chart/chart';
export type {
    ChartState,
    NormalisedAnimationOptions,
    NormalisedAnnotationsOptions,
    NormalisedBackgroundOptions,
    NormalisedChartSyncOptions,
    NormalisedFlashOnUpdateOptions,
    NormalisedForegroundOptions,
    NormalisedNavigatorOptions,
    NormalisedRangesDropdown,
    NormalisedRangesOptions,
    NormalisedScrollbarOptions,
    NormalisedScrollbarOrientationOptions,
    NormalisedScrollbarThumbStyle,
    NormalisedScrollbarTrackStyle,
    NormalisedStatusBarLabelOptions,
    NormalisedStatusBarOptions,
    ResolvedChartOptions,
} from './chart/chartState';
export { FormatManager } from './chart/formatter/formatManager';
export type { TransferableResources } from './chart/chart';
export type { ChartService } from './chart/chartService';
export { numberAxisOptionsDefs } from './chart/axesOptionsDefs';
export {
    angleCategoryAxisOptionsDefs,
    angleNumberAxisOptionsDefs,
    ordinalTimeAxisOptionsDefs,
    radiusCategoryAxisOptionsDefs,
    radiusNumberAxisOptionsDefs,
} from './chart/axesOptionsEnterpriseDefs';
export {
    standaloneChartOptionsDefs,
    topologyChartOptionsDefs,
    initialStatePickedOptionsDef,
} from './chart/chartOptionsDefs';
export {
    annotationCalloutStylesDefs,
    annotationChannelTextDefs,
    annotationCommentStylesDefs,
    annotationCrossLineStyleDefs,
    annotationDisjointChannelStyleDefs,
    annotationFibonacciStylesDefs,
    annotationLineStyleDefs,
    annotationLineTextDefs,
    annotationMeasurerStylesDefs,
    annotationNoteStylesDefs,
    annotationOptionsDef,
    annotationParallelChannelStyleDefs,
    annotationQuickMeasurerStylesDefs,
    annotationShapeStylesDefs,
    annotationTextStylesDef,
} from './chart/themes/annotationOptionsDef';
export type { IDataSelectionService } from './chart/data/dataSelectionServiceTypes';
export { DataChangeDescription } from './chart/data/dataChangeDescription';
export type { IDataSetSelection } from './chart/data/dataSetSelectionTypes';
export {
    commonAxisThemeTemplate,
    parentLevelAxisThemeTemplate,
    titleAxisThemeTemplate,
} from './chart/themes/axisThemeTemplate';
export { commonChartThemeTemplate } from './chart/themes/chartThemeTemplate';
export {
    boxPlotSeriesThemeableOptionsDef,
    candlestickSeriesThemeableOptionsDef,
    chordSeriesThemeableOptionsDef,
    coneFunnelSeriesThemeableOptionsDef,
    funnelSeriesThemeableOptionsDef,
    heatmapSeriesThemeableOptionsDef,
    mapLineBackgroundSeriesThemeableOptionsDef,
    mapLineSeriesThemeableOptionsDef,
    mapMarkerSeriesThemeableOptionsDef,
    mapShapeBackgroundSeriesThemeableOptionsDef,
    mapShapeSeriesThemeableOptionsDef,
    nightingaleSeriesThemeableOptionsDef,
    ohlcSeriesThemeableOptionsDef,
    organizationSeriesThemeableOptionsDef,
    pyramidSeriesThemeableOptionsDef,
    radarAreaSeriesThemeableOptionsDef,
    radarLineSeriesThemeableOptionsDef,
    radialBarSeriesThemeableOptionsDef,
    radialColumnSeriesThemeableOptionsDef,
    rangeAreaSeriesThemeableOptionsDef,
    rangeBarSeriesThemeableOptionsDef,
    sankeySeriesThemeableOptionsDef,
    sunburstSeriesThemeableOptionsDef,
    treemapSeriesThemeableOptionsDef,
    waterfallSeriesThemeableOptionsDef,
} from './chart/themes/enterpriseThemeableOptionsDef';
export {
    calculateSegments,
    predictCartesianFinancialAxis,
    predictCartesianNonPrimitiveAxis,
} from './chart/series/cartesian/util';
export { hasDimmedOpacity } from './chart/series/util';
export { stackCartesianSeries } from './chart/cartesianUtil';
export { CartesianCrossLine } from './chart/crossline/cartesianCrossLine';
export type {
    ActiveLoadMementoEvent,
    AxisLayout,
    AxisInteractionDragEvent,
    AxisInteractionMouseEvent,
    AxisInteractionMouseEnterEvent,
    AxisInteractionMouseLeaveEvent,
    AxisInteractionUpdateEvent,
    AxisInteractionWheelEvent,
    CollapsedRestoreEvent,
    ContextMenuEvent,
    DataModelDiff,
    DataModelDiffEvent,
    EventsHub,
    EventsHubMap,
    HighlightChangeEvent,
    HighlightNodeDatum,
    LayoutCompleteEvent,
    ScrollbarWheelEvent,
    SeriesAreaClickEvent,
    SeriesAreaContextMenuEvent,
    SeriesAreaHoverEvent,
    SeriesKeyNavCollapseEvent,
    SeriesKeyNavExpandEvent,
    SeriesKeyNavZoomEvent,
    SeriesKeyNavPanXEvent,
    ZoomInteractionRequestAxisWheelEvent,
    ZoomInteractionAxisDragEvent,
    ZoomInteractionAxisMouseEvent,
    ZoomInteractionAxisWheelEvent,
    ZoomInteractionWheelEvent,
    ZoomChangeCompleteEvent,
    ZoomChangeRequestEvent,
    ZoomChangeState,
    ZoomEventSourceDetail,
    ZoomLoadMementoEvent,
    ZoomPanStartEvent,
    ZoomSaveMementoEvent,
} from './core/eventsHub';
export { ChartOptions } from './module/optionsModule';
export type {
    AxisBandDatum,
    AxisContext,
    AxisFormattableLabel,
    AxisValuePick,
    PolarAxisLayout,
} from './module/axisContext';
export type { ChartRegistry, ChartAxisRegistry, ChartSeriesRegistry } from './module/moduleContext';
export { type AxisID, type DynamicContext, type DynamicContextApi, createDynamicContext } from 'ag-charts-core';
export type { SelectionModuleFns } from './chart/modulesManager';
export { Background } from './chart/background/background';
export { SeriesArea } from './chart/series-area/seriesArea';
export type { BackgroundRegion } from './chart/background-regions/backgroundRegion';
export { ChartAxes } from './chart/chartAxes';
export { NiceMode, resetAxisLabelSelectionFn } from './chart/axis/axisUtil';
export type { TickDatum } from './chart/axis/axisUtil';
export { generateTicks } from './chart/axis/generateTicks';
export { DataController } from './chart/data/dataController';
export { DataModel, fixNumericExtent, getMissCount } from './chart/data/dataModel';
export type {
    ColumnValueType,
    DatumPropertyDefinition,
    GroupedData,
    ProcessedData,
    ProcessedOutputDiff,
    PropertyDefinition,
    ScopeProvider,
    UngroupedData,
} from './chart/data/dataModel';
export { DataSet, type TransactionCollectionState } from './chart/data/dataSet';
export { deepCloneDataSet, replaceDataSet } from './chart/data/dataSetUtil';
export type { DataChangeDescriptionListener } from './chart/data/dataChangeDescription';
export {
    accumulativeValueProperty,
    animationValidation,
    createDatumId,
    diff,
    groupAccumulativeValueProperty,
    keyProperty,
    LARGEST_KEY_INTERVAL,
    normaliseGroupTo,
    processedDataIsAnimatable,
    SMALLEST_KEY_INTERVAL,
    trailingAccumulatedValueProperty,
    valueProperty,
} from './chart/data/processors';
export type { PreSceneRenderEvent, ProcessDataEvent } from './core/eventsHub';
export {
    adjustLabelPlacement,
    barLabelDataContext,
    barLabelObstaclesFor,
    buildBarLabelCandidates,
    compassCandidatePlacement,
    createBarCandidateStyleResolver,
    createBarPositionedCandidateResolver,
    createCandidateStyleResolver,
    fitLabelToContainer,
    fitLabelToContainerAutoSize,
    getLabelStyles,
    insideBarLabelBounds,
    pickPlacementStyle,
    resolveBarLabelCandidate,
    styledBarLabelBox,
    toResolvedPlacement,
    updateLabelNode,
} from './chart/labelUtil';
export type {
    BarCandidateStyleResolver,
    BarLabelDataContext,
    BarLabelPlacement,
    BarLabelSurface,
    BarPositionedCandidate,
    CandidatePlacementMapper,
    ResolvedLabelPlacement,
} from './chart/labelUtil';
export { LayoutElement } from './chart/layout/layoutManager';
export type { LayoutContext } from './chart/layout/layoutManager';
export { AnnotationManager } from './chart/annotation/annotationManager';
export { ContextMenuRegistry } from './chart/interaction/contextMenuRegistry';
export type {
    ContextShowOnMap,
    ContextMenuBuiltins,
    ContextMenuCallback,
    ContextMenuItemContract,
    ContextMenuItemContractNonRecursive,
} from './chart/interaction/contextMenuTypes';
export type { DragInterpreterClickEvent, DragInterpreterDblClickEvent } from './chart/interaction/dragInterpreter';
export { HighlightManager } from './chart/interaction/highlightManager';
export { InteractionManager, InteractionState } from './chart/interaction/interactionManager';
export { TooltipManager } from './chart/interaction/tooltipManager';
export {
    userInteraction,
    type UpdateZoomSourcing,
    type UpdateZoomWithFunction,
    ZoomManager,
} from './chart/interaction/zoomManager';
export type { CoreZoomState, CoreZoomStateSafeRetrieval, UpdateZoomChanges } from './chart/interaction/zoomManager';
export { PanToBBoxScalingModeEnum } from './util/panToBBox';
export { getItemId } from './chart/series/pickManager';
export { Series, SeriesNodePickMode } from './chart/series/series';
export type {
    MarkerStyleApply,
    MarkerStyleCompute,
    PickFocusInputs,
    PickFocusOutputs,
    SeriesNodeDataContext,
    SeriesNodePickMatch,
    SeriesNodeStyleContext,
    SeriesDataEvent,
    UnknownSeries,
} from './chart/series/series';
export { resetLabelFn, seriesLabelFadeInAnimation, seriesLabelFadeOutAnimation } from './chart/series/seriesLabelUtil';
export type { SeriesItemHighlightStyle } from './chart/series/seriesProperties';
export {
    FillGradientDefaults,
    FillImageDefaults,
    FillPatternDefaults,
    HighlightProperties,
    SeriesProperties,
    toHighlightString,
    toSelectionString,
    isUnselected,
    SeriesSelectionProperties,
} from './chart/series/seriesProperties';
export { SeriesMarker } from './chart/series/seriesMarker';
export { makeSeriesTooltip, SeriesTooltip } from './chart/series/seriesTooltip';
export type {
    DatumIndex,
    ErrorBoundSeriesNodeDatum,
    ISeries,
    ISeriesProperties,
    ItemId,
    SeriesNodeDatum,
    SeriesNodeEventTypes,
} from './chart/series/seriesTypes';
export { HighlightState, SelectionState } from './chart/series/seriesTypes';
export { getItemStyles, getItemStylesPerItemId, visibleRangeIndices, findNodeDatumInArray } from './chart/series/util';
export { AbstractBarSeries, AbstractBarSeriesProperties } from './chart/series/cartesian/abstractBarSeries';
export type {
    AbstractBarSeriesAnimationData,
    AbstractBarSeriesNodeDataContext,
    AbstractBarSeriesTypes,
} from './chart/series/cartesian/abstractBarSeries';
export {
    CartesianSeries,
    CartesianSeriesProperties,
    DEFAULT_CARTESIAN_DIRECTION_KEYS,
    DEFAULT_CARTESIAN_DIRECTION_NAMES,
} from './chart/series/cartesian/cartesianSeries';
export type { CartesianAnimationData } from './chart/series/cartesian/cartesianSeries';
export type {
    CartesianAnimationDataOf,
    CartesianBarLikeContext,
    CartesianCreateNodeDataContext,
    CartesianMarkerLikeContext,
    CartesianSeriesNodeDataContext,
    CartesianSeriesNodeDatum,
    CartesianSeriesPropertiesBase,
    CartesianSeriesTypes,
    ContextOf,
    CreateNodeDataContextOf,
    DatumOf,
    DatumSelectionOf,
    LabelOf,
    LabelSelectionOf,
    NodeOf,
    OptionsOf,
    PropertiesOf,
    StackContextOf,
} from './chart/series/cartesian/cartesianSeriesTypes';
export {
    interpolatePoints,
    plotInterpolatedLinePathStroke,
    plotLinePathStroke,
    prepareLinePathPropertyAnimation,
} from './chart/series/cartesian/lineUtil';
export type { LinePathSpan, LineSpanPointDatum, SpanAnimation } from './chart/series/cartesian/lineUtil';
export { CollapseMode, pairUpSpans } from './chart/series/cartesian/lineInterpolationUtil';
export {
    checkCrisp,
    collapsedStartingBarPosition,
    computeBarFocusBounds,
    midpointStartingBarPosition,
    prepareBarAnimationFunctions,
    resetBarSelectionsDirect,
    resetBarSelectionsFn,
} from './chart/series/cartesian/barUtil';
export { upsertNodeDatum } from './chart/series/cartesian/cartesianSeriesUtil';
export type { IncrementalUpdateContext } from './chart/series/cartesian/cartesianSeriesUtil';
export { plotAreaPathFill, prepareAreaFillAnimationFns } from './chart/series/cartesian/areaUtil';
export { calculateDataDiff } from './chart/series/cartesian/diffUtil';
export {
    computeMarkerFocusBounds,
    getMarkerStyles,
    cartesianMarkerDrawMode,
    markerFadeInAnimation,
    markerSwipeScaleInAnimation,
    maxMarkerStrokePickInflation,
    resetMarkerFn,
    resetMarkerPositionFn,
    resetMarkerSelectionsDirect,
} from './chart/series/cartesian/markerUtil';
export type { MarkerDrawMode } from './chart/series/cartesian/markerUtil';
export {
    buildResetPathFn,
    pathFadeInAnimation,
    pathSwipeInAnimation,
    updateClipPath,
} from './chart/series/cartesian/pathUtil';
export { addHitTestersToQuadtree, findQuadtreeMatch } from './chart/series/cartesian/quadtreeUtil';
export type { QuadtreeCompatibleNode } from './chart/series/cartesian/quadtreeUtil';
export { DataModelSeries } from './chart/series/dataModelSeries';
export type {
    DataModelSeriesConstructorOpts,
    DataModelSeriesNodeDataContext,
    DataModelSeriesNodeDatum,
} from './chart/series/dataModelSeries';
export {
    DEFAULT_POLAR_DIRECTION_KEYS,
    DEFAULT_POLAR_DIRECTION_NAMES,
    PolarSeries,
} from './chart/series/polar/polarSeries';
export type { PolarAnimationData } from './chart/series/polar/polarSeries';
export { HierarchyNode, HierarchySeries } from './chart/series/hierarchy/hierarchySeries';
export {
    HierarchyHighlightState,
    HierarchySeriesProperties,
    toHierarchyHighlightString,
} from './chart/series/hierarchy/hierarchySeriesProperties';
export { MercatorScale } from './chart/series/topology/mercatorScale';
export type { GaugeSeries } from './chart/series/gaugeSeries';
export { getShapeFill, getShapeStyle } from './chart/series/shapeUtil';
export type { ShapeFillBBox } from './chart/series/shapeUtil';
export { AggregationManager } from './chart/series/aggregationManager';
export {
    BucketLookupManager,
    IndexSetBucketLookupManager,
    SplitBucketLookupManager,
} from './chart/series/bucketLookupFeature';
export type { BucketLookupFeature } from './chart/series/seriesTypes';
export { Axis, AxisGroupZIndexMap } from './chart/axis/axis';
export type { AxisTickFormatParams, LabelNodeDatum } from './chart/axis/axis';
export { createAxisLabelFormatterCache, formatAxisLabelValue, getAxisLabelSideFlag } from './chart/axis/axisLabelUtil';
export type { AxisLabelFormatterCache } from './chart/axis/axisLabelUtil';
export { SeriesLabelProperties } from './chart/series/seriesLabelProperties';
export type { TickInterval } from './chart/axis/axisTick';
export { PolarAxis } from './chart/axis/polarAxis';
export { CategoryAxis } from './chart/axis/categoryAxis';
export { CartesianAxis } from './chart/axis/cartesianAxis';
export { DiscreteTimeAxis } from './chart/axis/discreteTimeAxis';
export { minimumTimeAxisDatumGranularity } from './chart/axis/timeAxis';
export type { ChartAxis, FormatDatumParams } from './chart/chartAxis';
export { getCrossLineValue, validateCrossLineValue } from './chart/crossline/crossLine';
export type { CrossLine, CrossLineType, PolarCrossLine } from './chart/crossline/crossLine';
export { getCrossLinesPlugin } from './chart/crossline/getCrossLinesPlugin';
export { calculateLabelTranslation } from './chart/crossline/crossLineLabelPosition';
export { bandRangeExpansion, isValidScaleValue } from './chart/scaleValue';
export {
    buildColorCategoryLegendData,
    buildGradientLegendDatum,
    colorScaleLegendFormatterContext,
    type CategoryLegendDatum,
    type ChartLegendDatum,
    type ChartLegendType,
    type ColorScaleLegendFormatterContext,
    type GradientLegendDatum,
    type GradientLegendNamedLabel,
} from './chart/legend/legendDatum';
export type { LegendSymbolOptions } from './chart/legend/legendSymbol';
export { isTooltipValueMissing } from './chart/tooltip/tooltip';
export type { TooltipContent, TooltipContentDataRow } from './chart/tooltip/tooltip';
export { PHASE_METADATA } from './motion/animation';
export type { AnimationPhase, AnimationValue } from './motion/animation';
export { resetMotion } from './motion/resetMotion';
export { fromToMotion, NODE_UPDATE_STATE_TO_PHASE_MAPPING } from './motion/fromToMotion';
export type { ExtraOpts, ApplyFn, FromToFns } from './motion/fromToMotion';
export { pathMotion } from './motion/pathMotion';
export { DOMManager } from './dom/domManager';
export { DOMElementProxy } from './dom/domElementProxy';
export { QuadtreeNearest } from './scene/util/quadtree';
export { UnitTimeScale } from './scale/unitTimeScale';
export { LogScale } from './scale/logScale';
export { BandScale } from './scale/bandScale';
export { CategoryScale } from './scale/categoryScale';
export { TimeScale } from './scale/timeScale';
export { ColorScale } from './scale/colorScale';
export { configureColorScale } from './scale/colorScaleUtil';
export { LinearScale } from './scale/linearScale';
export type { SyncGroupState, SyncDerivedDomain, SyncAxisLike, SyncChartLike } from './chart/interaction/syncManager';

export { DropShadow } from './scene/dropShadow';
export { Node, PointerEvents } from './scene/node';
export type { RenderContext } from './scene/node';
export { Rotatable, Translatable, Transformable, Scalable } from './scene/transformable';
export { Selection } from './scene/selection';
export type { SelectionInterface } from './scene/selection';
export { type GradientParams } from './scene/gradient/gradient';
export { ColorScaleProperties, getColorStops, StopProperties } from './scene/gradient/stops';
export { sectorBox } from './scene/util/sector';
export { drawCorner } from './scene/util/corner';
export type { Corner } from './scene/util/corner';
export type { ShapeLineCap, ShapeColor } from './scene/shape/shape';
export { SvgPath, TranslatableSvgPath, RotatableSvgPath } from './scene/shape/svgPath';
export { Text, RotatableText, TransformableText } from './scene/shape/text';
export { ContinuousScale } from './scale/continuousScale';
export { OrdinalTimeScale } from './scale/ordinalTimeScale';
export { ApproximateOrdinalTimeScale } from './scale/approximateOrdinalTimeScale';
export { APPROXIMATE_THRESHOLD } from './scale/discreteTimeScale';
export {
    Label,
    LabelPlacementStyle,
    LabelStyle,
    PlacedSeriesLabel,
    expandLabelBoxExtent,
    expandPlacementLabelBoxExtent,
    placedLabelTextOffset,
    resolvePlacementLabelBoxExtent,
    styledLabelTextOffset,
} from './chart/label';
export { Marker } from './chart/marker/marker';
export { drawMarkerUnitPolygon } from './chart/marker/shapes';
export { SectorBox } from './scene/sectorBox';
export { Image } from './scene/image';
export { ExtendedPath2D } from './scene/extendedPath2D';

export const motion = { ...fromToMotion, ...resetMotion };
export type { NodeUpdateState, FromToMotionPropFn } from './motion/fromToMotion';

export { Caption } from './chart/caption';
export { BBox } from './scene/bbox';
export { Group, TranslatableGroup, ScalableGroup, TransformableGroup } from './scene/group';
export { Scene } from './scene/scene';
export { Arc } from './scene/shape/arc';
export { Line } from './scene/shape/line';
export { Range } from './scene/shape/range';
export { Path } from './scene/shape/path';
export { SegmentedPath, type Segment } from './scene/shape/segmentedPath';
export { RadialColumnShape, getRadialColumnWidth } from './scene/shape/radialColumnShape';
export { Rect, clippedRoundRect, type CornerRadii } from './scene/shape/rect';
export { Sector } from './scene/shape/sector';
export { Shape, type CanvasContext } from './scene/shape/shape';
export { GroupWidget } from './widget/groupWidget';
export { NativeWidget } from './widget/nativeWidget';
export { SliderWidget } from './widget/sliderWidget';
export { ToolbarWidget } from './widget/toolbarWidget';
export type {
    DragWidgetEvent,
    HoverLikeEvent,
    KeyboardWidgetEvent,
    MouseWidgetEvent,
    WheelWidgetEvent,
    WidgetEvent,
} from './widget/widgetEvents';
export { Menu } from './components/menu/menu';
export type { MenuItem } from './components/menu/menu';
export { AnchoredPopover } from './components/popover/anchoredPopover';
export type { AnchoredPopoverOptions } from './components/popover/anchoredPopover';
export { DraggablePopover } from './components/popover/draggablePopover';
export type { PopoverConstructorOptions, PopoverOptions } from './components/popover/popover';
export { ToolbarButtonWidget } from './components/toolbar/toolbarButtonWidget';
export { BaseToolbar, Toolbar } from './components/toolbar/toolbar';
export type { ToolbarButtonOptions, ToolbarEventMap } from './components/toolbar/toolbar';
export { FloatingToolbar } from './components/toolbar/floatingToolbar';
export type { FloatingToolbarAnchor } from './components/toolbar/floatingToolbar';
export type { LocaleManager } from './locale/localeManager';
export { pickSectorsInBBoxPredicate } from './chart/series/polar/pieUtil';
